import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import pluralize from 'pluralize';

import scrollToDomClass from 'common/util/scrollToDomClass';
import { hasRoleAdmin } from 'common/userRoles';

import {
  PRE_LEASE_LIVE_STATUS_LIVE,
  PRE_LEASE_LIVE_STATUS_CLOSE_DUE_DATE,
  PRE_LEASE_LIVE_STATUS_PAST_DATE,
  PRE_LEASE_LIVE_STATUS_END,
} from 'common/preLeaseConstants';

import { calculatePreLeaseLiveStatus } from 'common/preLeaseHelpers';

import ButtonDeprecated, {
  BUTTON_THEME_YELLOW_GREYCLIFF,
} from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrLabel from 'client/components/SrLabel/SrLabel';
import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrTextGreycliff from 'client/components/SrTextGreycliff';

const SrPreLeaseStatsView = ({ title, weekCount, totalCount, onViewClick, isAdmin }) => {
  return (
    <SrBox>
      <span>
        <SrTextGreycliff weight="800" lineHeight="30px" color="blueGreycliff" opacity={0.5}>
          {title}
        </SrTextGreycliff>
        {onViewClick && isAdmin && (
          <SrTextGreycliff
            weight="700"
            lineHeight="30px"
            color="blue300"
            cursor="pointer"
            onClick={onViewClick}
          >
            {' View'}
          </SrTextGreycliff>
        )}
      </span>
      <br />
      <SrText font="greycliff" fontSize="21px" color="blueGreycliff" weight="800" display="block">
        {weekCount}
        <SrText font="greycliff" fontSize="13px" color="blueGreycliff" weight="800">
          &nbsp;in 7d
        </SrText>
      </SrText>
      <SrText
        font="greycliff"
        fontSize="21px"
        color="blueGreycliff"
        lineHeight="30px"
        weight="800"
        opacity="0.5"
        display="block"
      >
        {totalCount}
        <SrText
          font="greycliff"
          fontSize="13px"
          color="blueGreycliff"
          lineHeight="30px"
          weight="800"
        >
          &nbsp;total
        </SrText>
      </SrText>
    </SrBox>
  );
};

const SrPreLeaseTimelineBox = ({ title, subTitle, color }) => {
  return (
    <SrBox border="1px solid #D4D6D8" padding="26px 27px" borderRadius="12px">
      <SrTextGreycliff weight="800" lineHeight="30px" color="greyMonochrome">
        {title}
      </SrTextGreycliff>
      <br />
      <SrTextGreycliff weight="800" size="21" lineHeight="30px" color={color}>
        {subTitle}
      </SrTextGreycliff>
    </SrBox>
  );
};

const SrPreLeaseStatsCard = ({
  preLease,
  listing,
  stats,
  onUpdatePreLease,
  onEndPreLease,
  onViewLeads,
  onViewWaitlist,
  onViewTours,
  currentUser,
  currentPartner,
  scheduledLeadToursCount,
}) => {
  const preLeaseLiveStatus = calculatePreLeaseLiveStatus({ preLease, listing });

  let preLeasingTitleDOM = null;
  let preLeaseEndingRangeDateDOM = null;
  let preLeaseWarningTextDOM = null;
  let preLeaseCTAButtonsDOM = null;

  const preLeaseCompletedDate = moment(preLease.completedDate).startOf('day');
  const preLeaseDuration = listing.daysOnMarket - moment().diff(preLeaseCompletedDate, 'days');
  const isAdmin = currentUser && hasRoleAdmin(currentUser.roles);
  const showLeadsViewLink =
    isAdmin || currentPartner.experienceConfiguration?.showLeadChatConversations;

  if (
    preLeaseLiveStatus === PRE_LEASE_LIVE_STATUS_LIVE ||
    preLeaseLiveStatus === PRE_LEASE_LIVE_STATUS_CLOSE_DUE_DATE ||
    preLeaseLiveStatus === PRE_LEASE_LIVE_STATUS_PAST_DATE
  ) {
    const preLeaseEndingRangeDateText = [
      moment(preLease.startDate).format('MMM D'),
      moment(preLease.endDate).format('MMM D'),
    ].join(' - ');

    preLeasingTitleDOM = (
      <SrLabel
        justifyCenter
        color="blue300"
        backgroundColor="#CCE4FF"
        label="Currently Pre-Leasing: Tours are disabled"
      />
    );

    preLeaseEndingRangeDateDOM = (
      <SrPreLeaseTimelineBox
        title="Est. Ending date"
        subTitle={preLeaseEndingRangeDateText}
        color={
          preLeaseLiveStatus === PRE_LEASE_LIVE_STATUS_CLOSE_DUE_DATE
            ? 'orange300'
            : preLeaseLiveStatus === PRE_LEASE_LIVE_STATUS_PAST_DATE
            ? 'red'
            : null
        }
      />
    );

    preLeaseCTAButtonsDOM = (
      <>
        <SrLabel
          justifyCenter
          color="blue300"
          borderColor="#007aff"
          label="Update Pre-Lease"
          onClick={onUpdatePreLease}
        />
        <SrBox height="16px" />
        <ButtonDeprecated theme={BUTTON_THEME_YELLOW_GREYCLIFF} onClick={onEndPreLease}>
          Enable Tours (End Pre-Lease)
        </ButtonDeprecated>
      </>
    );
  }

  if (
    preLeaseLiveStatus === PRE_LEASE_LIVE_STATUS_CLOSE_DUE_DATE ||
    preLeaseLiveStatus === PRE_LEASE_LIVE_STATUS_PAST_DATE
  ) {
    const preLeaseWarningTextChildren =
      preLeaseLiveStatus === PRE_LEASE_LIVE_STATUS_CLOSE_DUE_DATE
        ? `
        This property is currently within the estimated pre-lease end date range.
        Please take action by updating the date if the property is not ready,
        or enabling tours if the home is ready to show/move in.
      `
        : `
        The date-range provided that the home would be ready for tours has passed.
        If this home is now available, press Enable Tours below.
        If the home is not yet available, please press Update Pre-Lease and
        provide a new date that tours are available.
      `;

    preLeaseWarningTextDOM = (
      <SrBox margin="16px 0 0">
        <SrTextGreycliff
          color={preLeaseLiveStatus === PRE_LEASE_LIVE_STATUS_CLOSE_DUE_DATE ? 'orange300' : 'red'}
        >
          {preLeaseWarningTextChildren}
        </SrTextGreycliff>
      </SrBox>
    );
  }

  if (preLeaseLiveStatus === PRE_LEASE_LIVE_STATUS_END) {
    preLeaseEndingRangeDateDOM = (
      <SrPreLeaseTimelineBox
        title="Ending date"
        subTitle={moment(preLease.completedDate).format('MMM D')}
      />
    );
  }

  return (
    <SrBox
      bgColor="white"
      padding="21px 20px 36px"
      borderRadius="14px"
      boxShadow="0px 5px 21px rgba(25, 51, 64, 0.05)"
    >
      {scheduledLeadToursCount > 0 && (
        <SrBox
          padding="20px 0 0"
          cursor="pointer"
          onClick={() => scrollToDomClass('SrPartnerLeadsCard', 90)}
        >
          <SrLabel
            justifyCenter
            color="green"
            backgroundColor="rgba(0, 162, 56, 0.07)"
            fontSize="large"
            label={`${scheduledLeadToursCount} Scheduled ${pluralize(
              'Tour',
              scheduledLeadToursCount,
            )}`}
          />
        </SrBox>
      )}
      {preLeasingTitleDOM}
      <SrFlex justify="between" margin="13px 0 24px">
        <SrPreLeaseStatsView
          title="Views"
          weekCount={stats.views.weekCount}
          totalCount={stats.views.totalCount}
          isAdmin={isAdmin}
        />
        <SrPreLeaseStatsView
          title="Leads"
          weekCount={stats.leads.weekCount}
          totalCount={stats.leads.totalCount}
          onViewClick={onViewLeads}
          isAdmin={showLeadsViewLink}
        />
        <SrPreLeaseStatsView
          title="Waitlist"
          weekCount={stats.waitlist.weekCount}
          totalCount={stats.waitlist.totalCount}
          onViewClick={onViewWaitlist}
          isAdmin={isAdmin}
        />
        {stats.tours && (
          <SrPreLeaseStatsView
            title="Tours"
            weekCount={stats.tours.weekCount}
            totalCount={stats.tours.totalCount}
            onViewClick={onViewTours}
            isAdmin={isAdmin}
          />
        )}
      </SrFlex>
      <div className="preleasingTimeline">
        <SrTextGreycliff weight="800" size="24" lineHeight="29px">
          Pre-leasing timeline
        </SrTextGreycliff>
        <SrBox margin="10px 0 20px">
          <SrGridRow columns={2} columnGap="18px">
            <SrPreLeaseTimelineBox
              title="Total Days Pre-Leasing"
              subTitle={preLease.completedDate ? preLeaseDuration : listing.daysOnMarket}
            />
            {preLeaseEndingRangeDateDOM}
          </SrGridRow>
          {preLeaseWarningTextDOM}
        </SrBox>
        {preLeaseCTAButtonsDOM}
      </div>
    </SrBox>
  );
};

SrPreLeaseStatsCard.propTypes = {
  listing: PropTypes.object.isRequired,
  preLease: PropTypes.object.isRequired,
  stats: PropTypes.object.isRequired,
  onUpdatePreLease: PropTypes.func,
  onEndPreLease: PropTypes.func,
};

SrPreLeaseStatsCard.displayName = 'SrPreLeaseStatsCard';

export default SrPreLeaseStatsCard;
